import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { Route, Routes, useNavigate } from 'react-router-dom'

import { Loader } from '@shared'
import authGql from '@graphql/queries/auth'
import { cookie } from '@utils'
import { ensureFreshCsrf, refreshCsrf } from '@utils/csrf-bootstrap'

// VERX-514: cap CSRF refetch/retry so a poisoned cross-app cookie can't loop forever
const MAX_CSRF_RETRIES = 3

const PageRoute = ({ authenticated, component: Component, allowedRoles, auth, loading, redirectRoles, redirectLocation, ...rest }) => {
  const systemNavigate = useNavigate()

  useEffect(() => {
    if (user && allowedRoles && !allowedRoles.includes(user.systemRole.name)) {
      return (
        systemNavigate(-1, { replace: true })
      )
    }
  }, [allowedRoles])

  const [loadingCSRF, setLoadingCSRF] = useState(true)
  const [csrfRetries, setCsrfRetries] = useState(0)

  useEffect(() => {
    ensureFreshCsrf()
      .catch(() => {})
      .then(() => {
        setLoadingCSRF(false)
      })
  }, [cookie.get('csrfCookie')])

  const authQuery = useQuery(authGql, {
    skip: loadingCSRF
  })

  if (!authQuery?.loading && authQuery?.error?.networkError?.statusCode === 403) {
    // VERX-514: On dev/stage a VerX session sharing the parent domain
    // (.dev.xevant.io / .stage.xevant.io) can poison X4's CSRF cookie, so the
    // 403 -> refetch-csrf -> retry loop never recovers. Cap the retries and
    // surface an actionable error instead of flooding the API forever.
    if (csrfRetries >= MAX_CSRF_RETRIES) {
      return (
        <div
          role='alert'
          style={{
            maxWidth: 480,
            margin: '15% auto',
            padding: '24px',
            textAlign: 'center',
            fontFamily: 'inherit'
          }}
        >
          <h2 style={{ marginBottom: 12 }}>Session conflict</h2>
          <p style={{ marginBottom: 20 }}>
            We couldn&apos;t verify your session because another Xevant app is
            signed in on this browser. Please clear cookies for this site and
            reload the page.
          </p>
          <button type='button' onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      )
    }

    refreshCsrf().catch(() => {})
    setCsrfRetries((count) => count + 1)
    setLoadingCSRF(true)
    return null
  }

  if (!loadingCSRF && !cookie.get('csrfCookie')) {
    setLoadingCSRF(true)
    return <Loader />
  }

  const isDataLogicPageSiteSwitching = localStorage.getItem('siteSwitching') === 'true' && window.location.href.includes('datalogic')

  if (authQuery.loading || loadingCSRF || isDataLogicPageSiteSwitching) {
    return (
      <Loader />
    )
  }

  if (loading) {
    return null
  }

  const user = authQuery?.data?.auth

  if (authenticated && !user) {
    window.location.href = '/login'
  }

  if (allowedRoles && !allowedRoles.includes(user?.systemRole.name)) {
    return systemNavigate(-1, { replace: true })
  }

  if (redirectRoles.includes(user?.systemRole.name)) {
    return systemNavigate(redirectLocation, { replace: true })
  }

  /* WD-461 - SHORT-TERM SOLUTION: ADDED TO SUPPORT MULTIPLE CLIENTS ACCESSING DEMO SITES */
  if (user?.client?.name?.includes('Demo') && !(user?.systemRole?.name === 'Superuser' || user?.systemRole?.name === 'Xevant Admin')) {
    if (Component.name === 'Settings' || Component.name === 'Users') {
      return (
        systemNavigate(-1, { replace: true })
      )
    }
  }
  /* END WD-461 - SHORT-TERM SOLUTION: ADDED TO SUPPORT MULTIPLE CLIENTS ACCESSING DEMO SITES */

  return (
    <Routes>
      <Route
        element={<Component />}
        {...rest}
      />
    </Routes>
  )
}

PageRoute.propTypes = {
  authenticated: PropTypes.bool,
  allowedRoles: PropTypes.array,
  redirectRoles: PropTypes.array,
  redirectLocation: PropTypes.string,
  component: PropTypes.any,
  auth: PropTypes.object,
  loading: PropTypes.bool
}

PageRoute.defaultProps = {
  authenticated: false,
  loading: false,
  redirectRoles: [],
  redirectLocation: '/settings'
}

export default PageRoute
